import React, { useState, useEffect, useMemo, memo, useRef } from 'react';
import { ChevronDown, Bookmark, ChevronLeft, ChevronRight, Cloud, Newspaper, TrendingUp, Calendar, Clock, ArrowRight, Car } from 'lucide-react';
import mapboxgl from 'mapbox-gl';

const AZURE_OPENAI_ENDPOINT = process.env.REACT_APP_AZURE_OPENAI_ENDPOINT || "https://SECURE_BACKEND.openai.azure.com";
const AZURE_OPENAI_KEY = process.env.REACT_APP_AZURE_OPENAI_KEY || "SECURE_BACKEND";
const AZURE_DEPLOYMENT_NAME = process.env.REACT_APP_AZURE_DEPLOYMENT_NAME || "SECURE_BACKEND";

// Stock logo mapping
const STOCK_LOGOS = {
  'AAPL': 'https://logo.clearbit.com/apple.com',
  'MSFT': 'https://logo.clearbit.com/microsoft.com',
  'GOOGL': 'https://logo.clearbit.com/google.com',
  'AMZN': 'https://logo.clearbit.com/amazon.com',
  'TSLA': 'https://logo.clearbit.com/tesla.com',
  'META': 'https://logo.clearbit.com/meta.com',
  'NVDA': 'https://logo.clearbit.com/nvidia.com',
  'JPM': 'https://logo.clearbit.com/jpmorganchase.com',
  'V': 'https://logo.clearbit.com/visa.com',
  'WMT': 'https://logo.clearbit.com/walmart.com',
  'SPY': '/image/session-img32.png',
  'QQQ': '/image/session-img32.png',
  'DIA': '/image/session-img32.png'
};

// Generate realistic stock chart
const generateRealisticStockChart = (stock) => {
  const currentPrice = parseFloat(stock.price);
  const changePercent = parseFloat(stock.change?.replace(/[+%]/g, '')) || 0;
  const ticker = stock.symbol || stock.ticker;
  
  const seed = ticker.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const seededRandom = (index) => {
    const x = Math.sin(seed + index * 1000) * 10000;
    return x - Math.floor(x);
  };
  
  const baseVolatility = 0.15;
  const points = 12;
  const chartData = [];
  const openingPrice = currentPrice / (1 + (changePercent / 100));
  
  for (let i = 0; i < points; i++) {
    const progress = i / (points - 1);
    const noise = (seededRandom(i) - 0.5) * baseVolatility * (currentPrice * 0.005);
    const easedProgress = progress < 0.5 
      ? 2 * progress * progress 
      : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    
    let currentValue = openingPrice + (currentPrice - openingPrice) * easedProgress + noise;
    if (i === points - 1) currentValue = currentPrice;
    
    chartData.push({ price: currentValue });
  }
  
  const prices = chartData.map(d => d.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange = maxPrice - minPrice;
  const minHeight = 30;
  const maxHeight = 70;
  
  return chartData.map(point => ({
    price: point.price,
    height: priceRange === 0 ? 50 : minHeight + ((point.price - minPrice) / priceRange) * (maxHeight - minHeight)
  }));
};

// Enhanced Stock Card Component
const StockCard = memo(({ stock, index }) => {
  const chartData = useMemo(() => generateRealisticStockChart(stock), [stock.symbol, stock.price]);
  const ticker = stock.symbol || stock.ticker;
  const isUp = stock.isUp || (stock.change && stock.change.startsWith('+'));
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      animation: `slideInStock 0.5s ease-out ${index * 0.1}s both`
    }}>
      {/* Company Logo */}
      <div style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        background: 'white',
        border: '2px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <img 
          src={STOCK_LOGOS[ticker] || '/image/session-img32.png'}
          alt={ticker}
          style={{
            width: '32px',
            height: '32px',
            objectFit: 'contain'
          }}
          onError={(e) => {
            e.target.src = '/image/session-img32.png';
          }}
        />
      </div>

      {/* Animated Arrow */}
      <div style={{
        animation: 'arrowPulse 2s ease-in-out infinite',
        color: '#6b7280'
      }}>
        <ArrowRight size={20} />
      </div>

      {/* Stock Card */}
      <div style={{
        flex: 1,
        background: 'rgba(255, 255, 255, 0.7)',
        borderRadius: '12px',
        padding: '14px 16px',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        {/* Left: Info */}
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '4px',
            textAlign: 'left'
          }}>
            {ticker}
          </div>
          <div style={{
            fontSize: '13px',
            color: '#6b7280',
            marginBottom: '8px',
            textAlign: 'left'
          }}>
            ${stock.price}
          </div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '13px',
            fontWeight: '600',
            color: isUp ? '#10b981' : '#ef4444',
            background: isUp ? '#d1fae5' : '#fee2e2',
            padding: '4px 10px',
            borderRadius: '6px'
          }}>
            {stock.change}
          </div>
        </div>

        {/* Right: Mini Chart */}
        <div style={{
          width: '100px',
          height: '40px',
          display: 'flex',
          alignItems: 'flex-end',
          gap: '2px'
        }}>
          {chartData.map((point, i) => (
            <div 
              key={i}
              style={{ 
                flex: 1, 
                height: `${point.height}%`, 
                background: isUp ? '#10b981' : '#ef4444',
                borderRadius: '2px 2px 0 0',
                opacity: 0.6 + (i / 24),
                transition: 'height 0.3s ease'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

StockCard.displayName = 'StockCard';
// Stock Carousel Component with auto-transition
const StockCarousel = ({ stocks }) => {
  const [currentStockIndex, setCurrentStockIndex] = useState(0);

  useEffect(() => {
    if (stocks.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentStockIndex((prev) => (prev + 1) % stocks.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [stocks.length]);

  const currentStock = stocks[currentStockIndex];
  const chartData = useMemo(() => generateRealisticStockChart(currentStock), [currentStock.symbol, currentStock.price]);
  const ticker = currentStock.symbol || currentStock.ticker;
  const isUp = currentStock.isUp || (currentStock.change && currentStock.change.startsWith('+'));

  return (
    <>
      {/* This replaces the entire image section */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '260px',
        background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px 20px'
      }}>
        {/* Category Label */}
<div style={{
  position: 'absolute',
  top: '16px',
  left: '16px',
  background: 'white',
  borderRadius: '14px',
  padding: '6px 14px',  // ✅ Add padding
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  zIndex: 1,
  display: 'flex',      // ✅ Add flex
  alignItems: 'center',  // ✅ Add this
  gap: '6px'            // ✅ Add gap
}}>
  <TrendingUp size={12} color="#10b981" />  {/* ✅ ADD THIS LINE */}
  <span style={{
    fontSize: '10px',
    fontWeight: '600',
    color: 'black',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'block',
    textAlign: 'center'
  }}>
    Stocks
  </span>
</div>

        {/* Horizontal Layout: Logo -> Arrow -> White Container */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          width: '100%',
          maxWidth: '320px'
        }}>
          {/* Company Logo */}
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: 'white',
            border: '3px solid white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            flexShrink: 0
          }}>
            <img 
              src={STOCK_LOGOS[ticker] || '/image/session-img32.png'}
              alt={ticker}
              style={{
                width: '40px',
                height: '40px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.src = '/image/session-img32.png';
              }}
            />
          </div>

          {/* Animated Arrow pointing right */}
          <div style={{
            animation: 'arrowPulse 2s ease-in-out infinite',
            flexShrink: 0
          }}>
            <ArrowRight 
              size={24} 
              style={{ 
                color: isUp ? '#16a34a' : '#dc2626',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
              }} 
            />
          </div>

          {/* White Container with Stock Info */}
          <div style={{
            flex: 1,
            padding: '16px 20px',
            background: '#f3fffaff',
            borderRadius: '24px',
            border: '1px solid #b3dab1ff',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/* Left: Stock Info */}
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '2px' }}>
                  {ticker}
                </div>
                <div style={{ fontSize: '24px', fontWeight: '800', color: '#111827', marginBottom: '2px' }}>
                  ${currentStock.price}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: isUp ? '#16a34a' : '#dc2626'
                }}>
                  {currentStock.change}
                </div>
              </div>

              {/* Right: Mini Chart */}
              <svg width="60" height="35" style={{ marginLeft: '5px' }}>
                {chartData.map((point, i) => (
                  <rect
                    key={i}
                    x={i * 7.8}
                    y={45 - point.height}
                    width="5"
                    height={point.height}
                    fill={isUp ? '#22c55e' : '#ef4444'}
                    opacity={0.3 + (i / chartData.length) * 0.7}
                    rx="2.5"
                  />
                ))}
              </svg>
            </div>
          </div>
        </div>

        {/* Progress Dots */}
        {stocks.length > 1 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '6px', 
            marginTop: '20px',
            position: 'absolute',
            bottom: '16px'
          }}>
            {stocks.map((_, idx) => (
              <div
                key={idx}
                style={{
                  width: idx === currentStockIndex ? '18px' : '6px',
                  height: '6px',
                  borderRadius: '3px',
                  background: idx === currentStockIndex ? '#111827' : '#53c76cff',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes arrowPulse {
          0%, 100% {
            transform: translateX(0);
            opacity: 0.7;
          }
          50% {
            transform: translateX(4px);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

// Weather Carousel Component - animated emoji + temperature
const WeatherCarousel = ({ bullets }) => {
  const [weatherData, setWeatherData] = useState(null);

  // Parse weather info from bullets
  useEffect(() => {
    if (!bullets || bullets.length === 0) return;

    // Extract temperature and condition from bullets
    const tempBullet = bullets.find(b => b.includes('°F') || b.includes('°C'));
    const conditionBullet = bullets.find(b => 
      b.toLowerCase().includes('sunny') || 
      b.toLowerCase().includes('cloudy') || 
      b.toLowerCase().includes('rain') ||
      b.toLowerCase().includes('snow') ||
      b.toLowerCase().includes('clear') ||
      b.toLowerCase().includes('partly')
    );

    // Extract temperature number
    const tempMatch = tempBullet?.match(/(\d+)°/);
    const temperature = tempMatch ? tempMatch[1] : '72';

    // Determine weather emoji based on condition
    let emoji = '☀️'; // default sunny
    
    if (conditionBullet) {
      const conditionLower = conditionBullet.toLowerCase();
      if (conditionLower.includes('rain') || conditionLower.includes('shower')) {
        emoji = '🌧️';
      } else if (conditionLower.includes('cloud') || conditionLower.includes('overcast')) {
        emoji = '☁️';
      } else if (conditionLower.includes('partly')) {
        emoji = '⛅';
      } else if (conditionLower.includes('snow')) {
        emoji = '❄️';
      } else if (conditionLower.includes('storm') || conditionLower.includes('thunder')) {
        emoji = '⛈️';
      } else if (conditionLower.includes('clear')) {
        emoji = '☀️';
      } else if (conditionLower.includes('fog')) {
        emoji = '🌫️';
      }
    }

    setWeatherData({ temperature, emoji });
  }, [bullets]);

  if (!weatherData) return null;

  return (
    <>
      {/* This replaces the entire image section */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '260px',
        background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px 20px'
      }}>
        {/* Category Label */}
<div style={{
  position: 'absolute',
  top: '16px',
  left: '16px',
  background: 'white',
  borderRadius: '14px',
  padding: '6px 14px',  // ✅ Add padding
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  zIndex: 1,
  display: 'flex',      // ✅ Add flex
  alignItems: 'center',  // ✅ Add this
  gap: '6px'            // ✅ Add gap
}}>
  <Cloud size={12} color="#3b82f6" />  {/* ✅ ADD THIS LINE */}
  <span style={{
    fontSize: '10px',
    fontWeight: '600',
    color: 'black',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    display: 'block',
    textAlign: 'center'
  }}>
    Weather
  </span>
</div>

        {/* Horizontal Layout: Emoji and Temperature side by side */}
<div style={{
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  padding: '28px 26px',
  background: '#f3faffff',
  borderRadius: '20px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  border: '1px solid #e4edffff'
}}>
          {/* Animated Weather Emoji */}
          <div style={{
            fontSize: '56px',
            lineHeight: 1,
            animation: 'floatWeather 3s ease-in-out infinite'
          }}>
            {weatherData.emoji}
          </div>

          {/* Temperature */}
          <div style={{
            fontSize: '72px',
            fontWeight: '600',
            color: '#1066acff',
            lineHeight: 1
          }}>
            {weatherData.temperature}°
          </div>
        </div>
      </div>

      <style>{`
        @keyframes floatWeather {
          0%, 100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-3px) scale(1.08);
          }
        }
      `}</style>
    </>
  );
};
// News Carousel Component - UPDATED to use actual article data from briefData
const NewsCarousel = ({ bullets, briefData }) => {
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [newsArticles, setNewsArticles] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);

  // Smart placeholder function from SwipeableNewsCards
  const getSmartPlaceholder = (title, sourceName = '') => {
    const text = (title + ' ' + sourceName).toLowerCase();
    
    const categories = [
      {
        keywords: ['ai', 'tech', 'software', 'app', 'digital', 'crypto', 'blockchain', 'startup'],
        image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80'
      },
      {
        keywords: ['business', 'finance', 'market', 'stock', 'economy', 'investment', 'trade'],
        image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80'
      },
      {
        keywords: ['sports?', 'football', 'basketball', 'game', 'nba', 'nfl', 'soccer', 'baseball'],
        image: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&q=80'
      },
      {
        keywords: ['movie', 'music', 'entertainment', 'celebrity', 'film', 'actor', 'singer'],
        image: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80'
      },
      {
        keywords: ['health', 'medical', 'doctor', 'hospital', 'medicine', 'wellness'],
        image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80'
      },
      {
        keywords: ['politics', 'election', 'government', 'congress', 'senate', 'president'],
        image: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=800&q=80'
      },
      {
        keywords: ['climate', 'environment', 'weather', 'nature', 'earth', 'energy'],
        image: 'https://images.unsplash.com/photo-1569163139394-de4798aa62b5?w=800&q=80'
      }
    ];
    
    for (const category of categories) {
      for (const keyword of category.keywords) {
        if (text.includes(keyword)) {
          return category.image;
        }
      }
    }
    
    // Default: News/World
    return 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80';
  };

  // ✅ UPDATED: Load articles with proper image handling
  useEffect(() => {
    const loadNewsArticles = async () => {
      if (!bullets || bullets.length === 0) return;

      setIsLoadingImages(true);
      const articles = [];

      try {
        // ✅ PRIORITY 1: Check if we have full article data from briefData
        if (briefData?.moduleData?.news?.articles && briefData.moduleData.news.articles.length > 0) {
          console.log('✅ Using articles from briefData:', briefData.moduleData.news.articles);
          
          briefData.moduleData.news.articles.forEach((article, i) => {
            // Use actual article data with smart fallback
            articles.push({
              title: article.title || bullets[i],
              image: article.image || getSmartPlaceholder(article.title || bullets[i], article.source),
              source: article.source || article.sourceName || 'News',
              url: article.url || article.link || null,
              time: article.time || 'Just now',
              description: article.description || ''
            });
          });
          
          setNewsArticles(articles);
          setIsLoadingImages(false);
          return; // ✅ Exit early - we have the data!
        }

        // ✅ FALLBACK: If no article data, try to fetch from backend
        console.log('⚠️ No article data in briefData, fetching from backend...');
        
        for (let i = 0; i < Math.min(bullets.length, 3); i++) {
          const headline = bullets[i];
          
          try {
            // Use same backend as SwipeableNewsCards
            const searchQuery = headline.split(' ').slice(0, 6).join(' ');
            const response = await fetch(
              `https://us-central1-fir-7c9c9.cloudfunctions.net/performWebSearch?q=${encodeURIComponent(searchQuery)}&count=1`,
              { timeout: 5000 }
            );
            
            const data = await response.json();
            
            if (data.success && data.results?.[0]) {
              const result = data.results[0];
              articles.push({
                title: headline,
                image: result.image || result.thumbnail || getSmartPlaceholder(headline),
                source: result.domain || 'News',
                url: result.url || null,
                time: 'Just now',
                description: result.snippet || ''
              });
            } else {
              // Use smart placeholder
              articles.push({
                title: headline,
                image: getSmartPlaceholder(headline),
                source: 'News',
                url: null,
                time: 'Just now',
                description: ''
              });
            }
          } catch (err) {
            console.error('Failed to fetch image for headline:', headline, err);
            // Use smart placeholder on error
            articles.push({
              title: headline,
              image: getSmartPlaceholder(headline),
              source: 'News',
              url: null,
              time: 'Just now',
              description: ''
            });
          }
        }

        console.log('✅ Loaded news articles:', articles);
        setNewsArticles(articles);
      } catch (error) {
        console.error('Failed to load news articles:', error);
        // Final fallback with smart placeholders
        setNewsArticles(bullets.slice(0, 3).map((headline) => ({
          title: headline,
          image: getSmartPlaceholder(headline),
          source: 'News',
          url: null,
          time: 'Just now',
          description: ''
        })));
      } finally {
        setIsLoadingImages(false);
      }
    };

    loadNewsArticles();
  }, [bullets, briefData]);

  // Auto-rotate news every 4 seconds
  useEffect(() => {
    if (!newsArticles || newsArticles.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentNewsIndex((prev) => (prev + 1) % newsArticles.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [newsArticles?.length]);

  if (isLoadingImages || !newsArticles || newsArticles.length === 0) {
    // Loading skeleton
    return (
      <div style={{
        position: 'relative',
        width: '100%',
        height: '260px',
        overflow: 'hidden',
        background: 'linear-gradient(260deg, rgba(147, 197, 253, 1) 0%, rgba(96, 165, 250, 1) 53%, rgba(224, 242, 254, 0.97) 60%, rgba(191, 219, 254, 1) 60%, #dbeafeff 100%)'
      }}>
        <div style={{
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, rgba(240, 240, 240, 0.6) 25%, rgba(224, 224, 224, 0.8) 50%, rgba(240, 240, 240, 0.6) 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite'
        }} />
        
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>
      </div>
    );
  }

  const currentArticle = newsArticles[currentNewsIndex];

  return (
    <>
      <div style={{
        position: 'relative',
        width: '100%',
        height: '260px',
        overflow: 'hidden',
        background: '#000'
      }}>
        {/* News Image Background - matching SwipeableNewsCards */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          transition: 'opacity 0.5s ease'
        }}>
          <img
            src={currentArticle.image}
            alt={currentArticle.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'brightness(0.75)'
            }}
            onError={(e) => {
              // Fallback to smart placeholder if image fails
              e.target.src = getSmartPlaceholder(currentArticle.title, currentArticle.source);
            }}
          />
        </div>

        {/* Gradient Overlays - matching SwipeableNewsCards */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '80px',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 100%)',
          zIndex: 1
        }} />

        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '140px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)',
          zIndex: 1
        }} />

        {/* Category Label - matching SwipeableNewsCards style */}
        <div style={{
          position: 'absolute',
          top: '16px',
          left: '16px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '14px',
          padding: '6px 14px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <Newspaper size={12} color="#ef4444" />
          <span style={{
            fontSize: '10px',
            fontWeight: '700',
            color: '#000',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            News
          </span>
        </div>
        
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          {/* Story Counter - bottom right */}
          <div style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(10px)',
            padding: '4px 10px',
            borderRadius: '14px',
            fontSize: '9px',
            fontWeight: '600',
            color: 'white',
            zIndex: 10
          }}>
            {currentNewsIndex + 1}/{newsArticles.length}
          </div>
        </div>

        {/* Headline - bottom section like SwipeableNewsCards */}
        <div style={{
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '0',
          padding: '16px 20px 20px',
          zIndex: 2
        }}>
          <h3 style={{
            fontSize: '15px',
            fontWeight: '700',
            color: 'white',
            lineHeight: '1.4',
            textShadow: '0 2px 12px rgba(0,0,0,0.8)',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            margin: '0 0 8px 0',
            textAlign: 'left'
          }}>
            {currentArticle.title}
          </h3>

          {/* Read indicator */}
          <div style={{
            position: 'relative',
            width: '100%'
          }}>
            <div
              style={{
                position: 'absolute',
                bottom: '208px',
                right: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '9px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.8)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              <div
                style={{
                  width: '20px',
                  height: '2px',
                  background: 'rgba(255,255,255,0.6)'
                }}
              />
              Breaking
            </div>
          </div>
        </div>

        {/* Progress Dots - matching SwipeableNewsCards style */}
        {newsArticles.length > 1 && (
          <div style={{ 
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex', 
            justifyContent: 'center', 
            gap: '5px',
            zIndex: 3
          }}>
            {newsArticles.map((_, idx) => (
              <div
                key={idx}
                style={{
                  width: idx === currentNewsIndex ? '20px' : '5px',
                  height: '5px',
                  borderRadius: '3px',
                  background: idx === currentNewsIndex 
                    ? '#ffffff' 
                    : 'rgba(255,255,255,0.4)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onClick={() => setCurrentNewsIndex(idx)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoiZnJhbnpvMTAwMSIsImEiOiJjbTlkN2p3bGcwemg0MmxxM3dmenMzcHZtIn0.OawlpbrFzFwbmlgIOr2AIw';

const TrafficMapCarousel = ({ bullets, briefData, isPlaying }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isMapInitializing, setIsMapInitializing] = useState(true);
  const [animationPhase, setAnimationPhase] = useState(0);
  const animationInterval = useRef(null);
  const [mapOpacity, setMapOpacity] = useState(0);

  // Parse traffic data from bullets
  const trafficData = useMemo(() => {
    if (!bullets || bullets.length === 0) return null;

    const timeBullet = bullets.find(b => b.includes('minutes') || b.includes('mins'));
    const timeMatch = timeBullet?.match(/(\d+)\s*(minutes|mins)/i);
    const travelTime = timeMatch ? timeMatch[1] : '30';
    
    const hasDelay = bullets.some(b => 
      b.toLowerCase().includes('delay') || 
      b.toLowerCase().includes('longer') ||
      b.toLowerCase().includes('heavy traffic')
    );

    const origin = briefData?.trafficOrigin || { lat: 40.8168, lng: -74.1954 };
    const destination = briefData?.trafficDestination || { lat: 40.7128, lng: -74.0060 };

    return {
      travelTime,
      hasDelay,
      origin,
      destination
    };
  }, [bullets, briefData]);
const clearMapLayers = (map) => {
  if (!map) return;

  const layers = map.getStyle()?.layers || [];

  layers.forEach((layer) => {
    if (
      layer.id.startsWith("route") ||
      layer.id.includes("marker") ||
      layer.id.includes("source")
    ) {
      if (map.getLayer(layer.id)) map.removeLayer(layer.id);
    }
  });

  const sources = map.getStyle()?.sources || {};
  Object.keys(sources).forEach((src) => {
    if (
      src.startsWith("route") ||
      src.includes("marker") ||
      src.includes("source")
    ) {
      if (map.getSource(src)) map.removeSource(src);
    }
  });
};

  // Helper function to create PNG from SVG
  const createPinImage = (color) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 48;
      canvas.height = 60;
      const ctx = canvas.getContext('2d');

      // Draw pin drop shape
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(24, 10);
      ctx.quadraticCurveTo(16, 10, 16, 18);
      ctx.quadraticCurveTo(16, 28, 24, 50);
      ctx.quadraticCurveTo(32, 28, 32, 18);
      ctx.quadraticCurveTo(32, 10, 24, 10);
      ctx.fill();
      
      // Draw white center circle
      ctx.fillStyle = 'white';
      ctx.beginPath();
      ctx.arc(24, 18, 6, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw colored inner dot
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(24, 18, 3, 0, Math.PI * 2);
      ctx.fill();
      
      // Add shadow
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.beginPath();
      ctx.ellipse(24, 55, 8, 3, 0, 0, Math.PI * 2);
      ctx.fill();

      resolve(canvas.toDataURL());
    });
  };

  // Initialize map
  useEffect(() => {
    if (!trafficData || map.current) return;
    
    if (!mapContainer.current) {
      console.warn('⚠️ Map container not ready');
      return;
    }

    mapboxgl.accessToken = MAPBOX_TOKEN;

    try {
      const centerLng = (trafficData.origin.lng + trafficData.destination.lng) / 2;
      const centerLat = (trafficData.origin.lat + trafficData.destination.lat) / 2;

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [centerLng, centerLat],
        zoom: 20,
        interactive: false,
        attributionControl: false,
        pitch: 65,
        bearing: 20,
        // OPTIMIZED PERFORMANCE OPTIONS:
        preserveDrawingBuffer: false,
        refreshExpiredTiles: false,
        fadeDuration: 300, // Smooth transitions
        renderWorldCopies: false,
        trackResize: false,
        maxParallelImageRequests: 8
      });
map.current.on('style.load', () => {
  const layers = map.current.getStyle().layers;

  layers.forEach((layer) => {
    if (layer.type === 'symbol') {
      // Remove text labels
      map.current.setLayoutProperty(layer.id, 'text-field', '');

      // Remove Mapbox icons (POI, transit, parks, etc.)
      map.current.setLayoutProperty(layer.id, 'icon-image', '');
    }
  });
});


      map.current.on('load', async () => {
        console.log('✅ Map loaded successfully');
        
        // ✅ SMOOTH FADE-IN after load
          clearMapLayers(map.current);

        setTimeout(() => {
          setMapOpacity(1);
        }, 100);
        
        setTimeout(() => {
          setIsMapInitializing(false);
        }, 500);
        
        // Add route line
        map.current.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [
                [trafficData.origin.lng, trafficData.origin.lat],
                [trafficData.destination.lng, trafficData.destination.lat]
              ]
            }
          }
        });

        // Route outline
        map.current.addLayer({
          id: 'route-outline',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#1e40af',
            'line-width': 8,
            'line-opacity': 0.4
          }
        });

        // Route main line
        map.current.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': trafficData.hasDelay ? '#ef4444' : '#3b82f6',
            'line-width': 5,
            'line-opacity': 0.9
          }
        });

        // Create pin images
        const originPinImage = await createPinImage('#10b981');
        const destPinImage = await createPinImage('#ef4444');

        // Load origin pin
        const originImg = new Image();
        originImg.onload = () => {
          if (!map.current.hasImage('origin-pin')) {
            map.current.addImage('origin-pin', originImg);
          }
          
          map.current.addSource('origin-marker', {
            type: 'geojson',
            data: {
              type: 'Point',
              coordinates: [trafficData.origin.lng, trafficData.origin.lat]
            }
          });
          
          map.current.addLayer({
            id: 'origin-marker',
            type: 'symbol',
            source: 'origin-marker',
            layout: {
              'icon-image': 'origin-pin',
              'icon-size': 0.8,
              'icon-anchor': 'bottom',
              'icon-allow-overlap': true
            }
          });
        };
        originImg.src = originPinImage;

        // Load destination pin
        const destImg = new Image();
        destImg.onload = () => {
          if (!map.current.hasImage('destination-pin')) {
            map.current.addImage('destination-pin', destImg);
          }
          
          map.current.addSource('destination-marker', {
            type: 'geojson',
            data: {
              type: 'Point',
              coordinates: [trafficData.destination.lng, trafficData.destination.lat]
            }
          });
          
          map.current.addLayer({
            id: 'destination-marker',
            type: 'symbol',
            source: 'destination-marker',
            layout: {
              'icon-image': 'destination-pin',
              'icon-size': 0.8,
              'icon-anchor': 'bottom',
              'icon-allow-overlap': true
            }
          });
        };
        destImg.src = destPinImage;

        setIsMapLoaded(true);
      });

      map.current.on('error', (e) => {
        console.error('❌ MapBox error:', e);
        setIsMapLoaded(false);
        setIsMapInitializing(false);
      });

    } catch (error) {
      console.error('❌ Failed to initialize map:', error);
      setIsMapInitializing(false);
      return;
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [trafficData]);

  // ✅ SMOOTH animation sequence - pauses when audio is playing
  useEffect(() => {
    if (!isMapLoaded || !map.current || !trafficData) return;

    // Clear any existing intervals
    if (animationInterval.current) {
      clearInterval(animationInterval.current);
    }

    // If audio is playing, show static overview and stop animating
    if (isPlaying) {
      const bounds = new mapboxgl.LngLatBounds()
        .extend([trafficData.origin.lng, trafficData.origin.lat])
        .extend([trafficData.destination.lng, trafficData.destination.lat]);
      
      map.current.easeTo({
        bounds: bounds,
        padding: 60,
        pitch: 30,
        duration: 800, // Smooth transition
        easing: (t) => t * (2 - t) // ease-out quad
      });
      
      setAnimationPhase(0);
      return;
    }

    // Only animate when audio is paused
    const phases = [
      // Phase 0: Show full route
      () => {
        const bounds = new mapboxgl.LngLatBounds()
          .extend([trafficData.origin.lng, trafficData.origin.lat])
          .extend([trafficData.destination.lng, trafficData.destination.lat]);
        
        map.current.fitBounds(bounds, {
          padding: 60,
          pitch: 30,
          duration: 1500,
          essential: true,
          easing: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
        });
      },
      // Phase 1: Zoom to origin
      () => {
        map.current.flyTo({
          center: [trafficData.origin.lng, trafficData.origin.lat],
          zoom: 14,
          pitch: 50,
          bearing: 0,
          duration: 1800,
          essential: true,
          easing: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
        });
      },
      // Phase 2: Zoom to destination
      () => {
        map.current.flyTo({
          center: [trafficData.destination.lng, trafficData.destination.lat],
          zoom: 14,
          pitch: 50,
          bearing: 0,
          duration: 1800,
          essential: true,
          easing: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
        });
      }
    ];

    // Start animation sequence
    let phaseIndex = 0;
    phases[0]();

    animationInterval.current = setInterval(() => {
      phaseIndex++;
      if (phaseIndex >= phases.length) {
        phaseIndex = 0;
      }
      phases[phaseIndex]();
      setAnimationPhase(phaseIndex);
    }, 5000); // Slower interval for smoother experience

    return () => {
      if (animationInterval.current) {
        clearInterval(animationInterval.current);
      }
    };
  }, [isMapLoaded, trafficData, isPlaying]);

  if (!trafficData) return null;

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '260px',
      background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
      overflow: 'hidden'
    }}>
      {/* Category Label */}
      <div style={{
        position: 'absolute',
        top: '16px',
        left: '16px',
        background: 'white',
        borderRadius: '14px',
        padding: '6px 14px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e29106ff" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
        <span style={{
          fontSize: '10px',
          fontWeight: '600',
          color: 'black',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Traffic
        </span>
      </div>

      {/* ✅ BLUR LOADING OVERLAY - Shows before map loads */}
      {isMapInitializing && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(254, 215, 170, 0.95) 0%, rgba(253, 186, 116, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'fadeOut 0.5s ease-out forwards',
          animationDelay: '0.3s'
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            borderTop: '3px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}

      {/* Mapbox Container */}
      <div 
        ref={mapContainer} 
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: mapOpacity,
          transition: 'opacity 0.5s ease-in-out'
        }}
      />

      {/* Traffic Info Overlay */}
      <div style={{
        position: 'absolute',
        top: '16px',
        right: '16px',
        background: 'rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '12px 16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        zIndex: 10,
        minWidth: '80px',
        opacity: mapOpacity,
        transition: 'opacity 0.5s ease-in-out'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '500',
            color: trafficData.hasDelay ? '#dc2626' : '#16a34a',
            lineHeight: 1
          }}>
            {trafficData.travelTime}
          </div>
          <div style={{
            fontSize: '11px',
            fontWeight: '500',
            color: '#6b7280',
            textTransform: 'uppercase'
          }}>
            minutes
          </div>
        </div>
      </div>

      {/* Animation phase indicator - only show when not playing and map loaded */}
      {!isPlaying && isMapLoaded && !isMapInitializing && (
        <div style={{
          position: 'absolute',
          bottom: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '6px',
          zIndex: 10,
          opacity: mapOpacity,
          transition: 'opacity 0.5s ease-in-out'
        }}>
          {[0, 1, 2].map((phase) => (
            <div
              key={phase}
              style={{
                width: animationPhase === phase ? '20px' : '6px',
                height: '6px',
                borderRadius: '3px',
                background: animationPhase === phase ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            />
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes fadeOut {
          to {
            opacity: 0;
            pointer-events: none;
          }
        }
      `}</style>
    </div>
  );
};


export default function DailyBriefTranscriptModal({ 
  briefData,
  onClose,
  audioRef,
  currentTime,
  audioDuration,
  onTogglePlayPause,
  onSpeedChange,
  onRewind,
  onFastForward,
  isPodcastSaved
}) {
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
const [isTransitioning, setIsTransitioning] = useState(false);
const [isPlaying, setIsPlaying] = useState(false);
  // ✅ Show instant greeting + closing cards, generate rest in background
  useEffect(() => {
    if (briefData?.dialogue && audioDuration) {
      // Create instant greeting and closing slides
      const greetingSlide = createInstantGreetingSlide();
      const closingSlide = createInstantClosingSlide();
      
      // Show greeting immediately
      setSlides([greetingSlide, closingSlide]);
      
      // Generate middle slides in background
      generateMiddleSlides(greetingSlide, closingSlide);
    }
  }, [briefData?.dialogue, audioDuration]);
// ✅ FIX #1: Apply speed whenever it changes
useEffect(() => {
  if (audioRef?.current) {
    audioRef.current.playbackRate = playbackSpeed;
    console.log('🎚️ Daily Brief speed changed to:', playbackSpeed);
  }
}, [playbackSpeed, audioRef]);

// ✅ FIX #2: Apply speed when audio loads (important for Daily Brief)
useEffect(() => {
  const audio = audioRef?.current;
  if (!audio) return;

  const handleLoadedMetadata = () => {
    audio.playbackRate = playbackSpeed;
    console.log('🎚️ Daily Brief speed set on load:', playbackSpeed);
  };

  audio.addEventListener('loadedmetadata', handleLoadedMetadata);
  return () => audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
}, [playbackSpeed, audioRef]);

  // ✅ Instant greeting slide
  const changeSpeed = () => {
    const speeds = [1.0, 1.15, 1.25, 1.5];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackSpeed(nextSpeed);
  };

// '/image/session-img33.png', '/image/session-img32.png, '/image/session-img34.png, '/image/session-img31.png', '/image/session-img35.png','/image/session-img36.png','/image/session-img37.png','/image/session-img38.png','/image/session-img39.png', /image/session-img40.png',/image/session-img41.png'

const createInstantGreetingSlide = () => {
  const hour = new Date().getHours();
  let greeting = 'Good Morning';
  let timeOfDay = 'morning';
  let greetingImage = '/image/session-img31.png';
  let greetingIcon = '🌅';

  if (hour >= 12 && hour < 17) {
    greeting = 'Good Afternoon';
    timeOfDay = 'afternoon';
    greetingImage = '/image/session-img32.png';
    greetingIcon = '☀️';
  } else if (hour >= 17) {
    greeting = 'Good Evening';
    timeOfDay = 'evening';
    greetingImage = '/image/session-img33.png';
    greetingIcon = '🌙';
  }

  // ➤ ROTATING DESCRIPTION PHRASES
  const welcomePhrases = [
    `A fresh ${timeOfDay} briefing, just for you`,
    `Welcome to your ${timeOfDay} briefing`,
    `Here’s your ${timeOfDay} briefing—ready when you are`,
    `Welcome, your ${timeOfDay} briefing has started`,
  ];

  const description =
    welcomePhrases[Math.floor(Math.random() * welcomePhrases.length)];

  return {
    title: greeting,
    category: 'Daily Brief',
    categoryIcon: greetingIcon,
    description,
    gradient: 'linear-gradient(250deg, rgba(236, 244, 255, 1) 0%, rgba(207, 228, 255, 1) 53%, #fffffff5 60%, #e9f9ffff 60%, #eaf2ffff 100%)',
    iconBg: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    customImage: greetingImage,
    startTime: 0,
    endTime: audioDuration * 0.1,
    index: 0,
    isGreeting: true
  };
};

  // ✅ Instant closing slide
const createInstantClosingSlide = () => {
  return {
    title: 'That\'s Your Brief',
    category: 'Daily Brief',
    categoryIcon: '✅', // ✅ Add checkmark icon
    description: 'Have a great day ahead!',
    gradient: 'linear-gradient(260deg,rgba(253, 230, 138, 1) 0%, rgba(252, 211, 77, 1) 53%, rgba(255, 249, 227, 0.97) 60%, rgba(255, 238, 172, 1) 60%, #fff4c8ff 100%)',
    iconBg: 'linear-gradient(135deg, #e9d5ff 0%, #d8b4fe 100%)',
    customImage: '/image/session-img33.png',
    startTime: audioDuration * 0.9,
    endTime: audioDuration,
    index: 999,
    isClosing: true
  };
};
  // ✅ Generate middle slides in background
  const generateMiddleSlides = async (greetingSlide, closingSlide) => {
    setIsGenerating(true);
    
    try {
      const sections = groupDialogueIntoSections(briefData.dialogue);
      const generatedSlides = [];
      
      for (const section of sections) {
        if (section.type === 'intro') continue; // Skip intro
        
        const aiData = await generateAISummary(section.lines, section.type);
        
        generatedSlides.push({
          title: aiData.title || section.title,
          category: section.category,
          bullets: aiData.bullets || null,
          stockCards: aiData.stockCards || null,
          description: aiData.description || null,
          icon: section.icon,
          gradient: section.gradient,
          iconBg: section.iconBg,
          image: section.image,
          lines: section.lines,
          type: section.type
        });
      }
      
      // Calculate timestamps for middle slides
      const middleStartTime = greetingSlide.endTime;
      const middleEndTime = closingSlide.startTime;
      const middleDuration = middleEndTime - middleStartTime;
      
      const totalChars = generatedSlides.reduce((sum, slide) => 
        sum + (slide.lines?.reduce((s, line) => s + (line.line?.length || 0), 0) || 0), 0
      );
      
      let cumulativeChars = 0;
      const slidesWithTimestamps = generatedSlides.map((slide, index) => {
        const slideChars = slide.lines?.reduce((sum, line) => sum + (line.line?.length || 0), 0) || 0;
        const startTime = middleStartTime + (cumulativeChars / totalChars) * middleDuration;
        const endTime = middleStartTime + ((cumulativeChars + slideChars) / totalChars) * middleDuration;
        
        cumulativeChars += slideChars;
        
        return { ...slide, startTime, endTime, index: index + 1 };
      });
      
      // Combine: greeting + middle slides + closing
      closingSlide.index = slidesWithTimestamps.length + 1;
      setSlides([greetingSlide, ...slidesWithTimestamps, closingSlide]);
      setIsGenerating(false);
    } catch (error) {
      console.error('Failed to generate slides:', error);
      setIsGenerating(false);
    }
  };

  // ✅ AI Summary Generator
const generateAISummary = async (lines, sectionType) => {
  const text = lines.map(l => l.line).join(' ');
  
  // Check if we already have cached data in sessionStorage
  const cacheKey = `brief_${sectionType}_${briefData?.id || 'default'}`;
  const cached = sessionStorage.getItem(cacheKey);
  
  if (cached) {
    console.log(`Using cached data for ${sectionType}`);
    return JSON.parse(cached);
  }
  
  try {
    let userPrompt = '';
    
    if (sectionType === 'weather') {
      userPrompt = `Extract weather info and return JSON:
      { "title": "Brief 7-9 word summary", "bullets": ["Currently X°F", "High/Low", "Conditions", "Recommendations"] }
      
      Text: ${text}`;
    } else if (sectionType === 'news') {
      userPrompt = `Extract headlines and return JSON:
      { "title": "Brief 7-9 word summary", "bullets": ["headline 1", "headline 2", "headline 3"] }
      
      Text: ${text}`;
    } else if (sectionType === 'stocks') {
      userPrompt = `Extract stock info and return JSON:
      { "title": "Brief 7-9 word summary", "stockCards": [{ "symbol": "AAPL", "price": "150.25", "change": "+2.5%", "isUp": true }] }
      
      Text: ${text}`;
    } else if (sectionType === 'schedule') {
      userPrompt = `Extract schedule and return JSON:
      { "title": "Brief 7-9 word summary", "bullets": ["9:00 AM - Event", "2:00 PM - Meeting"] }
      
      Text: ${text}`;
    } else if (sectionType === 'traffic') {
  // Clean text: remove quotes and special characters that break JSON
  const cleanText = text.replace(/"/g, "'").replace(/[\n\r\t]/g, ' ');
  userPrompt = `Extract traffic info and return ONLY valid JSON with no extra text:
  { "title": "Brief summary under 50 chars", "bullets": ["Travel time: X minutes", "Traffic condition", "Delay info", "Route notes"] }
  
  Text: ${cleanText}`;
} else {
      userPrompt = `Summarize in JSON:
      { "title": "Brief 7-9 word summary", "description": "One clear sentence" }
      
      Text: ${text}`;
    }
    
    const response = await fetch(
      `${AZURE_OPENAI_ENDPOINT}/openai/deployments/${AZURE_DEPLOYMENT_NAME}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': AZURE_OPENAI_KEY
        },
        body: JSON.stringify({
          messages: [
            { role: 'system', content: 'Extract info into JSON format. Be concise.' },
            { role: 'user', content: userPrompt }
          ],
          temperature: 0.3,
          max_tokens: 200
        })
      }
    );
    
    const data = await response.json();
    let content = data.choices[0].message.content.trim();
    
    if (content.startsWith('```json')) {
      content = content.replace(/```json\s*/g, '').replace(/```\s*$/g, '');
    }
    
    const result = JSON.parse(content);
    
    // Cache the result
    sessionStorage.setItem(cacheKey, JSON.stringify(result));
    console.log(`Cached data for ${sectionType}`);
    
    return result;
  } catch (error) {
    console.error('AI failed:', error);
    return { title: text.slice(0, 60), bullets: [] };
  }
};

  // ✅ Group dialogue into sections
  const groupDialogueIntoSections = (dialogue) => {
    const sections = [];
    let currentSection = null;
    let sectionLines = [];
    
    dialogue.forEach((line) => {
      if (line.section && line.section !== currentSection) {
        if (currentSection && sectionLines.length > 0) {
          sections.push(createSection(currentSection, sectionLines));
        }
        currentSection = line.section;
        sectionLines = [line];
      } else {
        sectionLines.push(line);
      }
    });
    
    if (currentSection && sectionLines.length > 0) {
      sections.push(createSection(currentSection, sectionLines));
    }
    
    return sections;
  };

  // ✅ Create section metadata
  const createSection = (title, lines) => {
    const sectionLower = title.toLowerCase();

      if (sectionLower.includes('traffic') || sectionLower.includes('commute')) {
    return {
      title: 'Your Commute',
      category: 'Traffic',
      type: 'traffic',
      lines,
      gradient: 'linear-gradient(250deg, rgba(255, 243, 224, 1) 0%, rgba(255, 228, 196, 1) 53%, #fffffff5 60%, #fff9f0ff 60%, #fffaf5ff 100%)',
      iconBg: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
      icon: <Clock size={60} color="#f97316" /> // You'll need to import Clock from lucide-react
    };
  }
  
    if (sectionLower.includes('weather')) {
      return {
        title: 'Weather Forecast',
        category: 'Weather',
        type: 'weather',
        lines,
        gradient: 'linear-gradient(250deg, rgba(236, 244, 255, 1) 0%, rgba(207, 228, 255, 1) 53%, #fffffff5 60%, #e9f9ffff 60%, #eaf2ffff 100%)',
        iconBg: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
        icon: <Cloud size={60} color="#3b82f6" />
      };
    }
    
    if (sectionLower.includes('news')) {
      return {
        title: 'Top Headlines',
        category: 'News',
        type: 'news',
        lines,
        gradient: 'linear-gradient(260deg, rgba(215, 207, 251, 1) 0%, rgba(114, 155, 244, 1) 53%, rgba(252, 231, 243, 0.97) 60%, rgba(168, 219, 249, 1) 60%, #e7e9fcff 100%)',
        iconBg: 'linear-gradient(135deg, #e7e8fcff 0%, #cfe6fbff 100%)',
        image: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80',
        icon: <Newspaper size={60} color="#ec4899" />
      };
    }
    
    if (sectionLower.includes('stock')) {
      return {
        title: 'Market Update',
        category: 'Stocks',
        type: 'stocks',
        lines,
        gradient: 'linear-gradient(260deg, rgba(238, 255, 249, 1) 0%, rgba(184, 255, 235, 1) 53%, #fffffff5 60%, #e9fffbff 60%, #eafffcff 100%)',
        iconBg: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
        icon: <TrendingUp size={60} color="#10b981" />
      };
    }
    
    if (sectionLower.includes('schedule')) {
      return {
        title: 'Your Schedule',
        category: 'Calendar',
        type: 'schedule',
        lines,
        gradient: 'linear-gradient(250deg, rgba(243, 232, 255, 1) 0%, rgba(233, 213, 255, 1) 53%, #fffffff5 60%, #faf5ffff 60%, #fbf7ffff 100%)',
        iconBg: 'linear-gradient(135deg, #e9d5ff 0%, #d8b4fe 100%)',
        icon: <Calendar size={60} color="#8b5cf6" />
      };
    }
    
    return {
      title: title,
      category: 'Daily Brief',
      type: 'intro',
      lines,
      gradient: 'linear-gradient(250deg, rgba(236, 244, 255, 1) 0%, rgba(207, 228, 255, 1) 53%, #fffffff5 60%, #e9f9ffff 60%, #eaf2ffff 100%)',
      iconBg: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
      icon: <Bookmark size={60} color="#3b82f6" />
    };
  };

  // ✅ Sync slide with audio
  useEffect(() => {
    if (!isPlaying || slides.length === 0) return;
    
    const foundSlide = slides.findIndex(slide => 
      currentTime >= (slide.startTime || 0) && currentTime < (slide.endTime || Infinity)
    );
    
    if (foundSlide !== -1 && foundSlide !== currentSlide) {
      setCurrentSlide(foundSlide);
    }
  }, [currentTime, isPlaying, slides, currentSlide]);

// ✅ Manual navigation with smooth transition and skeleton
const goToSlide = (index) => {
  if (index < 0 || index >= slides.length) return;
  
  // Start transition - show skeleton
  setIsTransitioning(true);
  
  // Wait for fade out, then change slide and fade back in
  setTimeout(() => {
    setCurrentSlide(index);
    if (audioRef?.current && slides[index]) {
      audioRef.current.currentTime = slides[index].startTime || 0;
    }
  }, 250); // Change slide halfway through transition
  
  // End transition
  setTimeout(() => {
    setIsTransitioning(false);
  }, 500);
};
  const currentCard = slides[currentSlide] || {};
// Skeleton Loading Card
const SkeletonCard = () => {
  // Rotate through different gradients for visual variety
  const gradients = [
    'linear-gradient(250deg, rgba(236, 244, 255, 1) 0%, rgba(207, 228, 255, 1) 53%, #fffffff5 60%, #e9f9ffff 60%, #eaf2ffff 100%)', // Blue
    'linear-gradient(250deg, rgba(255, 230, 244, 1) 0%, rgba(255, 211, 249, 1) 53%, #fffffff5 60%, #fef9feff 60%, #fffafeff 100%)', // Pink
    'linear-gradient(260deg, rgba(238, 255, 249, 1) 0%, rgba(184, 255, 235, 1) 53%, #fffffff5 60%, #e9fffbff 60%, #eafffcff 100%)', // Green
    'linear-gradient(250deg, rgba(243, 232, 255, 1) 0%, rgba(233, 213, 255, 1) 53%, #fffffff5 60%, #faf5ffff 60%, #fbf7ffff 100%)' // Purple
  ];
  
  const [currentGradientIndex, setCurrentGradientIndex] = React.useState(0);
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentGradientIndex((prev) => (prev + 1) % gradients.length);
    }, 800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.4)',
      backdropFilter: 'blur(20px)',
      borderRadius: '24px',
      padding: '4px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.71)',
      transition: 'all 0.8s ease'
    }}>
      <div style={{
        background: gradients[currentGradientIndex],
        borderRadius: '20px',
        overflow: 'hidden',
        border: '1px solid rgba(196, 210, 253, 0.5)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        transition: 'background 0.8s ease'
      }}>
        {/* Skeleton Image */}
        <div style={{
          width: '100%',
          height: '260px',
          background: 'linear-gradient(90deg, rgba(240, 240, 240, 0.6) 25%, rgba(224, 224, 224, 0.8) 50%, rgba(240, 240, 240, 0.6) 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          position: 'relative'
        }}>
          {/* Skeleton Category Badge */}
          <div style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            width: '80px',
            height: '24px',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '14px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              width: '60%',
              height: '10px',
              margin: '7px auto',
              background: 'linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
              borderRadius: '2px'
            }} />
          </div>

          {/* Animated circles/shapes for visual interest */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.5)',
            animation: 'pulse 2s ease-in-out infinite'
          }} />
        </div>
        
        {/* Skeleton Content */}
        <div style={{
          padding: '24px 20px',
          background: gradients[currentGradientIndex],
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          transition: 'background 0.8s ease'
        }}>
          {/* Skeleton Title */}
          <div style={{
            width: '60%',
            height: '36px',
            borderRadius: '20px',
            background: 'linear-gradient(90deg, rgba(0, 0, 0, 0.7) 25%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.7) 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }} />
          
          {/* Skeleton Bullets */}
          {[1, 2, 3].map((i) => (
            <div key={i} style={{
              width: `${100 - (i * 5)}%`,
              height: '20px',
              borderRadius: '8px',
              background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.6) 25%, rgba(255, 255, 255, 0.9) 50%, rgba(255, 255, 255, 0.6) 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
              animationDelay: `${i * 0.1}s`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.1;
          }
        }
      `}</style>
    </div>
  );
};
return (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'flex-end'
  }}>
    <div style={{
      width: '100%',
      height: '95vh',
      backgroundColor: '#1a1a1a',
      borderRadius: '28px 28px 0 0',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderTop: '2px solid #8d8d8d',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Background Gradient Layers */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(107, 114, 128, 0.1) 100%)',
        opacity: 0.25,
        zIndex: 0
      }} />

      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(107, 114, 128, 0.1) 100%)',
        filter: 'blur(100px) saturate(1.2)',
        transform: 'scale(1.1)',
        zIndex: 0,
        opacity: 0.4
      }} />

      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4))',
        zIndex: 1
      }} />

      {/* Header - Dark Background */}
      <div style={{
        padding: '24px',
        background: 'rgba(0, 0, 0, 0.4)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        zIndex: 2
      }}>
        <button onClick={onClose} style={{
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          background: 'rgba(255, 255, 255, 0.1)',
          border: 'none',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
        >
          <ChevronDown size={20} color="#fff" />
        </button>

        <div style={{ textAlign: 'left', flex: 1, paddingLeft: '16px' }}>
          <div style={{
            fontSize: '11px',
            fontWeight: '700',
            color: '#ffffff',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Daily Brief
          </div>
          <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.8)', fontWeight: '500' }}>
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'short', 
              day: 'numeric' 
            })}
          </div>
        </div>

        <div style={{ width: '40px' }} />
      </div>

      {/* Slide Content */}
    <div style={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      position: 'relative',
      overflowY: 'auto',
      zIndex: 2
    }}>

      <div style={{ width: '100%', maxWidth: '480px' }}>
        <div style={{
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? 'scale(0.95)' : 'scale(1)',
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}>
          {/* Show skeleton when: loading, generating, OR transitioning */}
          {slides.length === 0 || isGenerating || isTransitioning ? (
            <SkeletonCard />
          ) : (
            <div style={{
              background: 'rgba(255, 255, 255, 0.4)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '4px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.71)'
            }}>
              <div style={{
                background: currentCard.gradient || 'linear-gradient(250deg, rgba(236, 244, 255, 1) 0%, rgba(207, 228, 255, 1) 53%, #fffffff5 60%, #e9f9ffff 60%, #eaf2ffff 100%)',
                borderRadius: '20px',
                overflow: 'hidden',
                border: '1px solid rgba(196, 210, 253, 0.5)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                {/* Image/Visual Section - Stocks, Weather, and News shown here */}
                {currentCard.stockCards && currentCard.stockCards.length > 0 ? (
                  <StockCarousel stocks={currentCard.stockCards} />
                ) : currentCard.type === 'weather' && currentCard.bullets ? (
                  <WeatherCarousel bullets={currentCard.bullets} />
                ) : currentCard.type === 'news' && currentCard.bullets ? (
                  <NewsCarousel bullets={currentCard.bullets } />
                ) : currentCard.type === 'traffic' && currentCard.bullets ? (  
                  <TrafficMapCarousel bullets={currentCard.bullets} briefData={briefData} />
                ) : (
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '260px',
                    overflow: 'hidden'
                  }}>
                    {currentCard.customImage ? (
                      <img 
                        src={currentCard.customImage}
                        alt={currentCard.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : currentCard.image ? (
                      <img 
                        src={currentCard.image}
                        alt={currentCard.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        background: currentCard.iconBg || 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {currentCard.icon}
                      </div>
                    )}

                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      left: '16px',
                      background: 'white',
                      borderRadius: '14px',
                      padding: '2px 10px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      zIndex: 1
                    }}>
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: '600',
                          color: 'black',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'block',
                          textAlign: 'center'
                        }}
                      >
                        {currentCard.category}
                      </span>
                    </div>

                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '100px',
                      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, transparent 100%)'
                    }} />
                  </div>
                )}

                {/* Content Section */}
                <div style={{
                  padding: '24px 20px',
                  background: currentCard.gradient || 'linear-gradient(250deg, rgba(236, 244, 255, 1) 0%, rgba(207, 228, 255, 1) 53%, #fffffff5 60%, #e9f9ffff 60%, #eaf2ffff 100%)',
                  minHeight: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px'
                }}>
                  {/* Title - Hide for news cards */}
                  {currentCard.type !== 'news' && (
                    <div style={{
                      alignSelf: 'flex-start',
                      background: '#090909',
                      borderRadius: '20px',
                      padding: '10px 18px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: 'white',
                        lineHeight: '1.3',
                        textAlign: 'left'
                      }}>
                        {currentCard.title}
                      </div>
                    </div>
                  )}

                  {/* Bullet Points - Simple and Left-aligned with bullet dots */}
                  {currentCard.bullets && currentCard.bullets.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      textAlign: 'left'
                    }}>
                      {currentCard.bullets.map((bullet, idx) => (
                        <div key={idx} style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#374151',
                          lineHeight: '1.6',
                          paddingLeft: '0',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px'
                        }}>
                          {/* Bullet dot */}
                          <span style={{
                            minWidth: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#374151',
                            marginTop: '7px',
                            flexShrink: 0
                          }} />
                          
                          {/* Text */}
                          <span style={{
                            flex: 1
                          }}>
                            {bullet}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Description fallback */}
                  {currentCard.description && !currentCard.bullets && !currentCard.stockCards && (
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '400',
                      color: '#1f2937',
                      lineHeight: '1.6',
                      textAlign: 'left'
                    }}>
                      {currentCard.description}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Progress Dots */}
        {slides.length > 0 && (
          <div style={{
            marginTop: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            {slides.map((_, index) => (
              <div
                key={index}
                onClick={() => goToSlide(index)}
                style={{
                  width: index === currentSlide ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  background: index === currentSlide ? '#ffffffff' : '#8d8d8d80',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
              />
            ))}
          </div>
        )}
      </div>

    </div>

      {/* Audio Controls - Curved Bottom matching CompaniesLiveStation */}
      <div style={{ 
        background: 'rgba(244, 251, 255, 0.15)',
        backdropFilter: 'blur(10px)',
        padding: '16px 20px 24px',
        borderTop: '1px solid rgba(255, 255, 255, 0.56)',
        borderRadius: '42px 42px 0 0',
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        {/* White Curved Container for Controls */}
        <div style={{
          background: '#ffffffff',
          borderRadius: '32px',
          padding: '8px 20px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(169, 177, 185, 0.3)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          animation: 'fadeInControls 0.5s ease-out'
        }}>
          <div style={{ position: 'relative' }}>
    <button
      onClick={changeSpeed}
      style={{
        padding: '12px 16px',
        borderRadius: '20px',
        background: '#f3f4f6',
        border: 'none',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        color: '#000',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
      onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
    >
      {playbackSpeed}x
    </button>

            {showSpeedMenu && (
              <div style={{
                position: 'absolute',
                bottom: '55px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'white',
                borderRadius: '16px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                padding: '8px',
                zIndex: 100,
                animation: 'fadeInControls 0.3s ease-out'
              }}>
                {[0.75, 1, 1.25, 1.5, 2].map(speed => (
                  <button
                    key={speed}
                    onClick={() => {
                      setPlaybackSpeed(speed);
                      setShowSpeedMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: playbackSpeed === speed ? '#f3f4f6' : 'transparent',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: playbackSpeed === speed ? '#000' : '#6b7280',
                      transition: 'all 0.2s'
                    }}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            )}
          </div>

          {currentSlide > 0 ? (
            <button onClick={() => goToSlide(currentSlide - 1)} style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: '#f3f4f6',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
            >
              <ChevronLeft size={20} color="#000" />
            </button>
          ) : (
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.3,
              pointerEvents: 'none'
            }}>
              <ChevronLeft size={24} color="#9ca3af" />
            </div>
          )}

          <button onClick={() => setIsPlaying(!isPlaying)} style={{
            width: '58px',
            height: '58px',
            borderRadius: '50%',
            background: '#000',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            {isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <rect x="6" y="5" width="4" height="14" rx="1.5"/>
                <rect x="14" y="5" width="4" height="14" rx="1.5"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          {currentSlide < slides.length - 1 ? (
            <button onClick={() => goToSlide(currentSlide + 1)} style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: '#f3f4f6',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
            >
              <ChevronRight size={20} color="#000" />
            </button>
          ) : (
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.3,
              pointerEvents: 'none'
            }}>
              <ChevronRight size={24} color="#9ca3af" />
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInControls {
          0% {
            opacity: 0;
            filter: blur(3px);
            transform: scale(0.95) translateY(8px);
          }
          100% {
            opacity: 1;
            filter: blur(0px);
            transform: scale(1) translateY(0px);
          }
        }
      `}</style>
    </div>
  </div>
);
}